import { useState, useCallback, createContext, useContext } from 'react'
import {
  CModal,
  CModalHeader,
  CModalTitle,
  CModalBody,
  CModalFooter,
  CButton,
} from '@coreui/react'

const ConfirmContext = createContext(null)

/**
 * Wrap your app (or a subtree) with <ConfirmProvider> to enable useConfirm().
 *
 * Usage in any child component:
 *   const confirm = useConfirm()
 *   const ok = await confirm('Delete this agent?')
 *   if (ok) { ... }
 *
 * Options:
 *   await confirm('Are you sure?', {
 *     title: 'Confirm Delete',
 *     confirmText: 'Delete',
 *     confirmColor: 'danger',
 *     cancelText: 'Cancel',
 *   })
 */
export function ConfirmProvider({ children }) {
  const [state, setState] = useState({
    visible: false,
    message: '',
    title: 'Confirm',
    confirmText: 'Confirm',
    confirmColor: 'danger',
    cancelText: 'Cancel',
    resolve: null,
  })

  const confirm = useCallback((messageOrOptions, options = {}) => {
    const normalized = (
      messageOrOptions &&
      typeof messageOrOptions === 'object' &&
      !Array.isArray(messageOrOptions) &&
      !('$$typeof' in messageOrOptions)
    )
      ? messageOrOptions
      : { ...options, message: messageOrOptions }

    return new Promise((resolve) => {
      setState({
        visible: true,
        message: normalized.message || '',
        title: normalized.title || 'Confirm',
        confirmText: normalized.confirmText || 'Confirm',
        confirmColor: normalized.confirmColor || 'danger',
        cancelText: normalized.cancelText || 'Cancel',
        resolve,
      })
    })
  }, [])

  const handleClose = useCallback((result) => {
    setState((prev) => {
      if (prev.resolve) prev.resolve(result)
      return { ...prev, visible: false, resolve: null }
    })
  }, [])

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <CModal
        visible={state.visible}
        onClose={() => handleClose(false)}
        alignment="center"
      >
        <CModalHeader>
          <CModalTitle>{state.title}</CModalTitle>
        </CModalHeader>
        <CModalBody>{state.message}</CModalBody>
        <CModalFooter>
          <CButton color="secondary" variant="ghost" onClick={() => handleClose(false)}>
            {state.cancelText}
          </CButton>
          <CButton color={state.confirmColor} onClick={() => handleClose(true)}>
            {state.confirmText}
          </CButton>
        </CModalFooter>
      </CModal>
    </ConfirmContext.Provider>
  )
}

export function useConfirm() {
  const confirm = useContext(ConfirmContext)
  if (!confirm) {
    throw new Error('useConfirm must be used within a <ConfirmProvider>')
  }
  return confirm
}
